import React, { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, Cancel, UploadFile } from "@mui/icons-material"; // Import icons from Material-UI
import { green, red } from "@mui/material/colors"; // Import colors for styling
import { IconButton, Tooltip } from "@mui/material"; // For styling upload button
import { getToken, base_url } from "../../config/config";

const MachineSkillsMatrix = () => {
  const [machines, setMachines] = useState([]);
  const [skills, setSkills] = useState([]);
  const [skillsMatrix, setSkillsMatrix] = useState([]);
  const [newSkill, setNewSkill] = useState(""); // State for new skill input
  const [newMachine, setNewMachine] = useState(""); // State for new machine input
  const [imageFiles, setImageFiles] = useState({}); // State to handle image files for each machine
  const [selectedMachine, setSelectedMachine] = useState(null); // State to handle selected machine for image popup

  // Fetch machines from the API
  useEffect(() => {
    const fetchMachines = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getAllMachines`);
        const machineData = response.data;
        setMachines(machineData);

        // Initialize the skills matrix based on machine skills
        const initialMatrix = skills.map((skill) =>
          machineData.map((machine) =>
            machine.skills.includes(skill) ? 1 : 0,
          ),
        );
        setSkillsMatrix(initialMatrix);
      } catch (error) {
        console.error("Error fetching machines:", error);
      }
    };

    fetchMachines();
  }, [skills]); // Re-run when skills are updated

  // Fetch skills from the API
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/getAllMachineSkills`);
        const skillNames = response.data.map((skill) => skill.name);
        setSkills(skillNames);
      } catch (error) {
        console.error("Error fetching skills:", error);
      }
    };

    fetchSkills();
  }, []);

  // Handle file selection for image upload and trigger upload automatically
  const handleFileChange = async (machineId, event) => {
    const file = event.target.files[0];
    setImageFiles({ ...imageFiles, [machineId]: file });

    if (file) {
      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await axios.put(
          `${base_url}/v1/updateMachine/${machineId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );
        console.log("Image uploaded successfully:", response.data);
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  };

  // Toggle skill value (0 or 1) when a cell is clicked
  const toggleSkill = async (rowIndex, colIndex) => {
    const updatedMatrix = skillsMatrix.map((row, rIndex) =>
      row.map((cell, cIndex) => {
        if (rIndex === rowIndex && cIndex === colIndex) {
          return cell === 1 ? 0 : 1; // Toggle between 0 and 1
        }
        return cell;
      }),
    );

    setSkillsMatrix(updatedMatrix);

    // Update the machine's skills in the database
    try {
      const skill = skills[rowIndex];
      const machine = machines[colIndex];
      let updatedSkills;

      if (updatedMatrix[rowIndex][colIndex] === 1) {
        // Add skill to machine's skills array
        updatedSkills = [...machine.skills, skill];
      } else {
        // Remove skill from machine's skills array
        updatedSkills = machine.skills.filter(
          (machineSkill) => machineSkill !== skill,
        );
      }

      // Update the machine model in the backend
      await axios.put(`${base_url}/v1/updateMachine/${machine._id}`, {
        skills: updatedSkills,
      });

      // Update local machine data to reflect the change
      const updatedMachines = [...machines];
      updatedMachines[colIndex].skills = updatedSkills;
      setMachines(updatedMachines);
    } catch (error) {
      console.error("Error updating machine skills:", error);
    }
  };

  // Add a new skill
  const addNewSkill = async () => {
    if (newSkill.trim() === "") return; // Prevent adding empty skill

    try {
      await axios.post(`${base_url}/v1/createMachineSkill`, { name: newSkill });
      setSkills([...skills, newSkill]); // Add the new skill to the local state
      setNewSkill(""); // Clear the input field
    } catch (error) {
      console.error("Error adding new skill:", error);
    }
  };

  // Add a new machine
  const addNewMachine = async () => {
    if (newMachine.trim() === "") return; // Prevent adding empty machine

    try {
      const response = await axios.post(`${base_url}/v1/createMachines`, {
        name: newMachine,
      });
      const newMachineData = response.data;
      setMachines([...machines, newMachineData]); // Add the new machine to the local state

      // Add an empty skill column for the new machine
      const newMatrixColumn = skills.map(() => 0);
      setSkillsMatrix(
        skillsMatrix.map((row, rowIndex) => [
          ...row,
          newMatrixColumn[rowIndex],
        ]),
      );
      setNewMachine(""); // Clear the input field
    } catch (error) {
      console.error("Error adding new machine:", error);
    }
  };

  // Handle Enter key press for adding new skill or machine
  const handleSkillKeyPress = (e) => {
    if (e.key === "Enter") {
      addNewSkill();
    }
  };

  const handleMachineKeyPress = (e) => {
    if (e.key === "Enter") {
      addNewMachine();
    }
  };

  // Handle image click to show popup
  const handleImageClick = (machine) => {
    setSelectedMachine(machine);
  };

  const containerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  };

  const tableWrapperStyle = {
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "auto",
  };

  const tableStyle = {
    borderCollapse: "collapse",
    minWidth: "600px",
    width: "100%",
    textAlign: "center",
  };

  const thTdStyle = {
    border: "1px solid #ccc",
    padding: "10px",
    textAlign: "center",
    wordWrap: "break-word",
    whiteSpace: "nowrap",
  };

  const skillNameStyle = {
    fontWeight: "bold",
    position: "sticky",
    left: 0,
    backgroundColor: "#f0f0f0",
    zIndex: 1,
  };

  const imageThumbnailStyle = {
    width: "150px",
    height: "150px",
    objectFit: "cover",
    cursor: "pointer",
    borderRadius: "8px",
    marginTop: "10px",
  };

  const popupImageStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "400px",
    height: "400px",
    zIndex: 9999,
    backgroundColor: "#fff",
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    borderRadius: "10px",
    textAlign: "center",
    display: selectedMachine ? "block" : "none",
  };

  const popupImageTextStyle = {
    padding: "10px",
    fontSize: "20px",
  };

  const overlayStyle = {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 9998,
    display: selectedMachine ? "block" : "none",
  };

  const closePopup = () => {
    setSelectedMachine(null);
  };

  return (
    <div style={containerStyle}>
      <div style={tableWrapperStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...thTdStyle, ...skillNameStyle }}></th>
              {machines.map((machine, index) => (
                <th key={index} style={thTdStyle}>
                  <div>
                    {machine.name}
                    {machine.image ? (
                      <div
                        onClick={() => handleImageClick(machine)}
                        style={{ marginTop: "10px" }}>
                        <img
                          src={`${base_url}/${machine.image}`}
                          alt={machine.name}
                          style={imageThumbnailStyle}
                        />
                        <Tooltip title='Upload Image'>
                          <IconButton component='label'>
                            <UploadFile />
                            <input
                              type='file'
                              hidden
                              onChange={(e) => handleFileChange(machine._id, e)}
                            />
                          </IconButton>
                        </Tooltip>
                      </div>
                    ) : (
                      <Tooltip title='Upload Image'>
                        <IconButton component='label'>
                          <UploadFile />
                          <input
                            type='file'
                            hidden
                            onChange={(e) => handleFileChange(machine._id, e)}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </th>
              ))}
              <th style={thTdStyle}>
                <input
                  type='text'
                  value={newMachine}
                  onChange={(e) => setNewMachine(e.target.value)}
                  onKeyPress={handleMachineKeyPress}
                  placeholder='Add new machine'
                />
                <button onClick={addNewMachine}>Add Machine</button>
              </th>
            </tr>
          </thead>
          <tbody>
            {skills.map((skill, rowIndex) => (
              <tr key={rowIndex}>
                <td style={{ ...thTdStyle, ...skillNameStyle }}>{skill}</td>
                {skillsMatrix[rowIndex] &&
                  skillsMatrix[rowIndex].map((skillValue, colIndex) => (
                    <td
                      key={colIndex}
                      style={{ ...thTdStyle }}
                      onClick={() => toggleSkill(rowIndex, colIndex)}>
                      {skillValue === 1 ? (
                        <CheckCircle style={{ color: green[500] }} /> // Green check icon
                      ) : (
                        <Cancel style={{ color: red[500] }} /> // Red cross icon
                      )}
                    </td>
                  ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...thTdStyle, ...skillNameStyle }}>
                <input
                  type='text'
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={handleSkillKeyPress}
                  placeholder='Add new skill'
                />
                <button onClick={addNewSkill}>Add Skill</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Full-size image popup */}
      {selectedMachine && (
        <>
          <div style={overlayStyle} onClick={closePopup}></div>
          <div style={popupImageStyle}>
            <img
              src={`${base_url}/${selectedMachine.image}`}
              alt={selectedMachine.name}
              style={{
                width: "100%",
                height: "80%",
                borderRadius: "10px 10px 0 0",
              }}
            />
            <div style={popupImageTextStyle}>{selectedMachine.name}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default MachineSkillsMatrix;
